import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { fetchApiDeleteWithBody, fetchApiPost } from "../utils";

export const ManualCfrStatusValues = [
  "sent",
  "cancelled",
  "revoked",
  "declined",
  "completed",
  "expired",
] as const;

export const ManualCfrStatusValidation = z.enum(ManualCfrStatusValues);

const OptionalManualCfrTextValidation = z
  .string()
  .trim()
  .transform((value: string) => value || undefined)
  .optional();

export const ManualCfrMetaFieldsValidation = z.object({
  contract_number: OptionalManualCfrTextValidation,
  contract_type: OptionalManualCfrTextValidation,
  contract_quantity: OptionalManualCfrTextValidation,
  contract_shipping_dates: OptionalManualCfrTextValidation,
  contract_date: OptionalManualCfrTextValidation,
});

export const CreateManualCfrRequestValidation = z.object({
  customer_id: z.string().uuid(),
  customer_destination_id: z.string().uuid(),
  commodity_type: z.string().trim().min(1),
  crop_year: z.number().int(),
  document_title: OptionalManualCfrTextValidation,
  meta_fields: ManualCfrMetaFieldsValidation.optional(),
  last_status: ManualCfrStatusValidation,
  final_document: z.object({ filename: z.string().trim().min(1) }),
  sent_document: z.object({ filename: z.string().trim().min(1) }).optional(),
});

export const ConfirmManualCfrSigningSessionUploadsValidation = z.object({
  signing_session_id: z.string().uuid(),
});

export const DeleteManualCfrRequestValidation = z.object({
  reason: z.string().trim().min(1, "A deletion reason is required"),
});

export const SoftDeleteManualCfrSigningSessionValidation =
  DeleteManualCfrRequestValidation.extend({
    cfr_document_instance_id: z.string().uuid(),
  });

export type ManualCfrStatus = z.infer<typeof ManualCfrStatusValidation>;
export type CreateManualCfrRequest = z.infer<typeof CreateManualCfrRequestValidation>;
export type ConfirmManualCfrSigningSessionUploadsRequest = z.infer<
  typeof ConfirmManualCfrSigningSessionUploadsValidation
>;
export type DeleteManualCfrRequest = z.infer<typeof DeleteManualCfrRequestValidation>;
export type SoftDeleteManualCfrSigningSessionRequest = z.infer<
  typeof SoftDeleteManualCfrSigningSessionValidation
>;

export interface ManualDocumentUpload {
  id: string;
  filename: string;
  key: string;
  bucket: string;
  region: string;
  content_type: "application/pdf";
  presigned_put_url: string;
}

export interface CreateManualCfrResult {
  cfr_document_instance_id: string;
  cfr_document_id: string;
  group_id: string;
  signing_session_id: string;
  document_template_instance_id: string;
  uploads: {
    final_document: ManualDocumentUpload;
    sent_document: ManualDocumentUpload;
  };
}

export interface ConfirmManualCfrResult {
  signing_session_id: string;
  last_status: ManualCfrStatus;
  sent_document_id: string;
  final_document_id: string;
}

export interface DeleteManualCfrResult {
  cfr_document_instance_id: string;
  signing_session_id: string;
  hidden_at: string;
}

export function useCreateManualCfrSigningSession() {
  return async (data: CreateManualCfrRequest) =>
    fetchApiPost<CreateManualCfrResult>(
      "contracts/cfr/manual-signing-sessions",
      data,
    );
}

export function useConfirmManualCfrSigningSessionUploads() {
  const queryClient = useQueryClient();

  return async (data: ConfirmManualCfrSigningSessionUploadsRequest) => {
    const result = await fetchApiPost<ConfirmManualCfrResult>(
      `contracts/cfr/manual-signing-sessions/${data.signing_session_id}/confirm`,
      {},
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["cfr", "contracts"] });
      queryClient.invalidateQueries({ queryKey: ["cfr"] });
    }

    return result;
  };
}

export function useSoftDeleteManualCfrSigningSession() {
  const queryClient = useQueryClient();

  return async (data: SoftDeleteManualCfrSigningSessionRequest) => {
    const result = await fetchApiDeleteWithBody<DeleteManualCfrResult>(
      `contracts/cfr/manual-signing-sessions/${data.cfr_document_instance_id}`,
      { reason: data.reason },
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["cfr", "contracts"] });
      queryClient.invalidateQueries({ queryKey: ["cfr"] });
    }

    return result;
  };
}
